import { useMemo, useState } from 'react'
import { unique } from 'lib/array/arrayUtils'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import {
  buildHomepageOfferCarouselClaimCacheKey,
  shouldHideHomepageCarouselDisplay,
  withHomepageOfferCarouselDisplayOfferIds,
  type HomepageCarouselDisplayDecision,
} from 'home/components/OfferCarousels/dedup/homepageOfferCarouselDeduplication'
import { useHomepageOfferCarouselScope } from 'home/components/OfferCarousels/dedup/HomepageOfferCarousel'
import { useHomepageOfferClaimStore } from 'home/components/OfferCarousels/dedup/HomepageOfferDedupProvider'

interface HomepageOfferCarouselExperienceList {
  ids: Array<string>
  fetching: boolean
  key: string
}

interface HomepageOfferCarouselListBaseOptions {
  minimumOffersCount?: number
  applyMinimumOffersCount?: boolean
  hideWhenEmpty?: boolean
  maxDisplayCount?: number
  recordOnly?: boolean
}

type HomepageOfferCarouselListSource =
  | { offerList: App.OfferList, experienceList?: never }
  | { experienceList: HomepageOfferCarouselExperienceList, offerList?: never }

type UseHomepageOfferCarouselListOptions =
  HomepageOfferCarouselListBaseOptions & HomepageOfferCarouselListSource

interface HomepageOfferCarouselListResult {
  isActive: boolean
  hidden: boolean
  fetching: boolean
  offerList?: App.OfferList
  experienceIds: Array<string>
}

interface ClaimedDisplay {
  inputKey: string
  candidateOfferIds: Array<string>
  decision: HomepageCarouselDisplayDecision
}

const EMPTY_DISPLAY_OFFER_IDS: Array<string> = []

const INACTIVE_HIDDEN_RESULT: HomepageOfferCarouselListResult = {
  isActive: false,
  hidden: true,
  fetching: false,
  offerList: undefined,
  experienceIds: EMPTY_DISPLAY_OFFER_IDS,
}

function areDisplayOfferIdsEqual(left: Array<string>, right: Array<string>): boolean {
  if (left === right) {
    return true
  }

  if (left.length !== right.length) {
    return false
  }

  return left.every((offerId, index) => offerId === right[index])
}

function useHomepageOfferCarouselList(
  options: UseHomepageOfferCarouselListOptions,
): HomepageOfferCarouselListResult {
  const {
    offerList,
    experienceList,
    minimumOffersCount,
    applyMinimumOffersCount = false,
    hideWhenEmpty,
    maxDisplayCount,
    recordOnly = false,
  } = options
  const candidateOfferIds = offerList?.offerIds ?? experienceList?.ids ?? EMPTY_DISPLAY_OFFER_IDS
  const sourceFetching = offerList?.fetching ?? experienceList?.fetching ?? false
  const listKey = offerList?.key ?? experienceList?.key ?? ''
  const scope = useHomepageOfferCarouselScope()
  const store = useHomepageOfferClaimStore()
  const [claimedDisplay, setClaimedDisplay] = useState<ClaimedDisplay | undefined>(undefined)

  const canUseDedup = !!scope && !!store
  const uniqueCandidateIds = useMemo(() => unique(candidateOfferIds), [candidateOfferIds])
  const uniqueCandidateCount = uniqueCandidateIds.length
  const hideOptions = {
    minimumOffersCount,
    applyMinimumOffersCount,
    hideWhenEmpty,
  }
  const canHideWithoutClaim = !sourceFetching && shouldHideHomepageCarouselDisplay(uniqueCandidateCount, hideOptions)
  const activeListKey = buildHomepageOfferCarouselClaimCacheKey({
    listKey,
    maxDisplayCount,
    applyMinimumOffersCount,
    minimumOffersCount,
    hideWhenEmpty,
  })
  const scopedListKey = `${activeListKey}:${scope?.carouselId ?? ''}:${scope?.deduplication.mode ?? ''}`
  const resolvedDecision = claimedDisplay?.inputKey === scopedListKey
    && areDisplayOfferIdsEqual(claimedDisplay.candidateOfferIds, candidateOfferIds)
    ? claimedDisplay.decision
    : undefined

  useIsomorphicLayoutEffect(() => {
    if (!scope || !store || sourceFetching || canHideWithoutClaim) {
      return
    }

    const nextDecision = store.claim({
      carouselId: scope.carouselId,
      deduplication: scope.deduplication,
      candidateOfferIds,
      maxDisplayCount,
      minimumOffersCount,
      applyMinimumOffersCount,
      hideWhenEmpty,
    })

    setClaimedDisplay((current) => {
      if (
        current
        && current.inputKey === scopedListKey
        && areDisplayOfferIdsEqual(current.candidateOfferIds, candidateOfferIds)
        && current.decision.hidden === nextDecision.hidden
        && areDisplayOfferIdsEqual(current.decision.displayOfferIds, nextDecision.displayOfferIds)
      ) {
        return current
      }

      return {
        inputKey: scopedListKey,
        candidateOfferIds,
        decision: nextDecision,
      }
    })
  }, [
    applyMinimumOffersCount,
    canHideWithoutClaim,
    candidateOfferIds,
    hideWhenEmpty,
    maxDisplayCount,
    minimumOffersCount,
    scopedListKey,
    scope,
    sourceFetching,
    store,
  ])

  const result = useMemo((): HomepageOfferCarouselListResult => {
    if (!canUseDedup) {
      const hidden = canHideWithoutClaim
      if (!offerList && hidden) {
        return INACTIVE_HIDDEN_RESULT
      }

      return {
        isActive: false,
        hidden,
        fetching: sourceFetching,
        offerList,
        experienceIds: uniqueCandidateIds,
      }
    }

    if (canHideWithoutClaim || resolvedDecision?.hidden) {
      return {
        isActive: true,
        hidden: true,
        fetching: sourceFetching,
        offerList: offerList ? {
          ...offerList,
          offerIds: EMPTY_DISPLAY_OFFER_IDS,
        } : undefined,
        experienceIds: EMPTY_DISPLAY_OFFER_IDS,
      }
    }

    if (recordOnly) {
      return {
        isActive: true,
        hidden: false,
        fetching: sourceFetching,
        offerList,
        experienceIds: uniqueCandidateIds,
      }
    }

    const pending = sourceFetching || !resolvedDecision
    const displayOfferIds = pending ? EMPTY_DISPLAY_OFFER_IDS : (resolvedDecision.displayOfferIds)
    const renderState = {
      isActive: true,
      ready: !pending,
      hidden: false,
      displayOfferIds,
    }

    return {
      isActive: true,
      hidden: false,
      fetching: sourceFetching || pending,
      offerList: offerList ? withHomepageOfferCarouselDisplayOfferIds(offerList, renderState) : undefined,
      experienceIds: pending ? EMPTY_DISPLAY_OFFER_IDS : displayOfferIds,
    }
  }, [
    canHideWithoutClaim,
    canUseDedup,
    offerList,
    recordOnly,
    resolvedDecision,
    sourceFetching,
    uniqueCandidateIds,
  ])

  return result
}

export default useHomepageOfferCarouselList
